import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  FileText, Plus, Search, Edit2, Trash2, Upload, FileUp,
  RefreshCw, X, Loader2, CheckCircle, AlertCircle, Minus, Download,
  History, Mail
} from 'lucide-react';
import EditHistoryModal from '../../components/EditHistoryModal';
import EmailModal from '../../components/EmailModal';
import EmailStatusBadge, { isUnemailed } from '../../components/EmailStatusBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ISRI Commodity Types
const ISRI_COMMODITIES = [
  'TALDON', 'TALC', 'TELIC', 'TALLY', 'TERSE', 'TAINT TABOR',
  'TENSE', 'TWITCH', 'ZORBA', 'TABLET', 'TRUMP', 'TEENS',
  'TASTE', 'TEPID', 'TREAD', 'TABOO', 'TAKES', 'TALON',
  'TROMA', 'TOUGH', 'TUTU', 'TWANG', 'TWIST'
];

export default function SalesContractDirectory() {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [message, setMessage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [historyContract, setHistoryContract] = useState(null);
  const [emailContract, setEmailContract] = useState(null);
  const [showOnlyUnemailed, setShowOnlyUnemailed] = useState(false);
  
  // Master data
  const [supplierOptions, setSupplierOptions] = useState([]);
  const [paymentTermsOptions, setPaymentTermsOptions] = useState([]);
  const [termsConditionsList, setTermsConditionsList] = useState([]); // For multi-select T&C
  const [nextContractNumber, setNextContractNumber] = useState('');
  
  const emptyItem = {
    commodity: '',
    quantity: '',
    quantityUnit: 'MT',
    rate: '',
    currency: 'USD',
    amount: ''
  };
  
  const [formData, setFormData] = useState({
    contractNumber: '',
    supplierId: '',
    supplierName: '',
    supplierAddress: '',
    supplierCountry: '',
    supplierEmail: '',
    contractDate: new Date().toISOString().split('T')[0],
    validFrom: '',
    validUntil: '',
    items: [{ ...emptyItem }],
    totalValue: '',
    paymentTerms: '',
    deliveryTerms: '',
    termsConditions: [], // Multi-select T&C
    portOfLoading: '',
    portOfDischarge: '',
    status: 'Active',
    remarks: '',
    pdfFileName: ''
  });

  useEffect(() => {
    fetchContracts();
    fetchSuppliers();
    fetchPaymentTerms();
    fetchTermsConditions();
    generateContractNumber();
  }, []);

  const fetchContracts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/imports/sales-contracts`);
      setContracts(response.data.data || []);
    } catch (error) {
      console.error('Error fetching contracts:', error);
      setContracts([]);
    }
    setLoading(false);
  };

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get(`${API}/master-data/raw-material/suppliers`);
      // Map the supplier data to consistent field names
      const suppliers = (response.data.data || []).map(s => ({
        id: s.id,
        supplierName: s.name || s.supplierName,
        address: s.address || '',
        country: s.country || '',
        city: s.city || '',
        code: s.code || '',
        email: s.email || ''
      }));
      setSupplierOptions(suppliers);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  };

  const fetchPaymentTerms = async () => {
    try {
      const response = await axios.get(`${API}/master-data/stores/payment_terms`);
      setPaymentTermsOptions(response.data.data || []);
    } catch (error) {
      console.error('Error fetching payment terms:', error);
    }
  };

  const fetchTermsConditions = async () => {
    try {
      const response = await axios.get(`${API}/master-data/stores/terms_conditions`);
      setTermsConditionsList(response.data.data || []);
    } catch (error) {
      console.error('Error fetching terms conditions:', error);
    }
  };

  const generateContractNumber = async () => {
    try {
      const response = await axios.get(`${API}/imports/sales-contracts/next-number`);
      setNextContractNumber(response.data.nextNumber);
    } catch (error) {
      // Generate fallback number
      const year = new Date().getFullYear();
      const month = String(new Date().getMonth() + 1).padStart(2, '0');
      setNextContractNumber(`SC-${year}${month}-001`);
    }
  };

  const handleSupplierChange = (supplierId) => {
    const supplier = supplierOptions.find(s => s.id === supplierId);
    if (supplier) {
      setFormData(prev => ({
        ...prev,
        supplierId: supplierId,
        supplierName: supplier.supplierName || '',
        supplierAddress: supplier.address || '',
        supplierCountry: supplier.country || '',
        supplierEmail: supplier.email || ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        supplierId: '',
        supplierName: '',
        supplierAddress: '',
        supplierCountry: '',
        supplierEmail: ''
      }));
    }
  };

  const handleItemChange = (index, field, value) => {
    setFormData(prev => {
      const newItems = [...prev.items];
      newItems[index] = { ...newItems[index], [field]: value };
      
      // Calculate amount when quantity or rate changes
      if (field === 'quantity' || field === 'rate') {
        const qty = parseFloat(newItems[index].quantity) || 0;
        const rate = parseFloat(newItems[index].rate) || 0;
        newItems[index].amount = (qty * rate).toFixed(2);
      }
      
      // Calculate total value
      const totalValue = newItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
      
      return { ...prev, items: newItems, totalValue: totalValue.toFixed(2) };
    });
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { ...emptyItem }]
    }));
  };

  const removeItem = (index) => {
    if (formData.items.length <= 1) return;
    setFormData(prev => {
      const newItems = prev.items.filter((_, i) => i !== index);
      const totalValue = newItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
      return { ...prev, items: newItems, totalValue: totalValue.toFixed(2) };
    });
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setMessage({ type: 'error', text: 'Please upload a PDF file' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }
    
    setUploading(true);
    setExtractedData(null);
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await axios.post(
        `${API}/imports/sales-contracts/extract-from-pdf`,
        formDataUpload,
        { 
          
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );
      
      if (response.data.success) {
        setExtractedData(response.data);
        
        const extracted = response.data.extractedData;
        
        // Find matching supplier
        const matchedSupplier = supplierOptions.find(s => 
          s.supplierName?.toLowerCase() === extracted.supplierName?.toLowerCase() ||
          response.data.supplierMatching?.suggestedMatch === s.supplierName
        );
        
        // Check if commodity is in ISRI list
        const extractedCommodity = extracted.commodity?.toUpperCase();
        const matchedCommodity = ISRI_COMMODITIES.find(c => 
          extractedCommodity?.includes(c) || c.includes(extractedCommodity?.split(' ')[0] || '')
        );
        
        // Create items from extracted data
        const items = [{
          commodity: matchedCommodity || '', // Leave blank if not found
          quantity: extracted.quantity || '',
          quantityUnit: extracted.quantityUnit || 'MT',
          rate: extracted.rate || '',
          currency: extracted.currency || 'USD',
          amount: (parseFloat(extracted.quantity || 0) * parseFloat(extracted.rate || 0)).toFixed(2)
        }];
        
        setFormData(prev => ({
          ...prev,
          contractNumber: nextContractNumber,
          supplierId: matchedSupplier?.id || '',
          supplierName: matchedSupplier?.supplierName || '',
          supplierAddress: matchedSupplier?.address || extracted.supplierAddress || '',
          supplierCountry: matchedSupplier?.country || extracted.supplierCountry || '',
          contractDate: extracted.contractDate || new Date().toISOString().split('T')[0],
          validFrom: extracted.validFrom || '',
          validUntil: extracted.validUntil || '',
          items: items,
          totalValue: extracted.totalValue || items[0].amount,
          paymentTerms: extracted.paymentTerms || '',
          deliveryTerms: extracted.deliveryTerms || '',
          portOfLoading: extracted.portOfLoading || '',
          portOfDischarge: extracted.portOfDischarge || '',
          remarks: extracted.remarks || '',
          pdfFileName: response.data.originalFileName || ''
        }));
        
        setMessage({ type: 'success', text: 'Contract details extracted! Please review and select correct values from dropdowns.' });
        setShowUploadModal(false);
        setShowForm(true);
      } else {
        setMessage({ type: 'error', text: response.data.error || 'Failed to extract contract details' });
      }
    } catch (error) {
      console.error('Upload error:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to process PDF' });
    }
    
    setUploading(false);
    setTimeout(() => setMessage(null), 5000);
  };

  const handleSubmit = async (e, forceDuplicate = false) => {
    if (e && e.preventDefault) e.preventDefault();
    try {
      const submitData = {
        ...formData,
        contractNumber: formData.contractNumber || nextContractNumber
      };

      if (editingContract) {
        await axios.put(`${API}/imports/sales-contracts/${editingContract.id}`, submitData);
        setMessage({ type: 'success', text: 'Contract updated successfully!' });
      } else {
        const url = forceDuplicate
          ? `${API}/imports/sales-contracts?forceDuplicate=true`
          : `${API}/imports/sales-contracts`;
        await axios.post(url, submitData);
        setMessage({ type: 'success', text: 'Contract created successfully!' });
      }
      fetchContracts();
      generateContractNumber();
      resetForm();
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail || error?.message || 'Failed to save contract';
      // 409 = backend's content-duplicate guard. Offer a Save-Anyway path.
      if (status === 409 && !editingContract) {
        setMessage({
          type: 'duplicate',
          text: detail,
          allowForce: true,
        });
        // No auto-dismiss for duplicate prompt — user must choose
        return;
      }
      setMessage({ type: 'error', text: detail });
      setTimeout(() => setMessage(null), 8000);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this contract?')) return;
    try {
      await axios.delete(`${API}/imports/sales-contracts/${id}`);
      setMessage({ type: 'success', text: 'Contract deleted successfully!' });
      fetchContracts();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to delete contract' });
    }
    setTimeout(() => setMessage(null), 3000);
  };

  // Download Purchase Order PDF for a contract
  const handleDownloadPO = async (contract) => {
    try {
      setMessage({ type: 'info', text: 'Generating PO...' });
      const response = await axios.post(
        `${API}/imports/sales-contracts/${contract.id}/generate-po?t=${Date.now()}`,
        {},
        {  responseType: 'blob', headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' } }
      );
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `PO-${contract.contractNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setMessage({ type: 'success', text: 'PO downloaded successfully!' });
    } catch (error) {
      console.error('Error downloading PO:', error);
      setMessage({ type: 'error', text: 'Failed to generate PO. Please try again.' });
    }
    setTimeout(() => setMessage(null), 3000);
  };

  const resetForm = () => {
    setFormData({
      contractNumber: '',
      supplierId: '',
      supplierName: '',
      supplierAddress: '',
      supplierCountry: '',
      supplierEmail: '',
      contractDate: new Date().toISOString().split('T')[0],
      validFrom: '',
      validUntil: '',
      items: [{ ...emptyItem }],
      totalValue: '',
      paymentTerms: '',
      deliveryTerms: '',
      termsConditions: [], // Reset multi-select T&C
      portOfLoading: '',
      portOfDischarge: '',
      status: 'Active',
      remarks: '',
      pdfFileName: ''
    });
    setEditingContract(null);
    setExtractedData(null);
    setShowForm(false);
  };

  const openAddForm = () => {
    resetForm();
    setFormData(prev => ({ ...prev, contractNumber: nextContractNumber }));
    setShowForm(true);
  };

  const filteredContracts = contracts.filter(contract => {
    const matchesSearch = !searchTerm || 
      contract.contractNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contract.supplierName?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || contract.status === statusFilter;
    const matchesUnemailed = !showOnlyUnemailed || isUnemailed(contract);
    return matchesSearch && matchesStatus && matchesUnemailed;
  });

  const unemailedCount = contracts.filter(isUnemailed).length;

  const getStatusColor = (status) => {
    const colors = {
      'Active': 'bg-green-100 text-green-700',
      'Expired': 'bg-red-100 text-red-700',
      'Pending': 'bg-yellow-100 text-yellow-700',
      'Completed': 'bg-blue-100 text-blue-700',
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  // Get items summary for table display
  const getItemsSummary = (items) => {
    if (!items || items.length === 0) return '-';
    if (items.length === 1) return `${items[0].commodity} (${items[0].quantity} ${items[0].quantityUnit})`;
    return `${items.length} items`;
  };

  // Detect duplicate contract numbers (legacy data created before uniqueness enforcement)
  const duplicateNumbers = React.useMemo(() => {
    const counts = {};
    contracts.forEach(c => {
      const n = (c.contractNumber || '').trim();
      if (n) counts[n] = (counts[n] || 0) + 1;
    });
    return new Set(Object.keys(counts).filter(n => counts[n] > 1));
  }, [contracts]);

  return (
    <div className="space-y-6" data-testid="sales-contract-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg">
            <FileText className="text-white" size={24} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Sales Contract Directory</h1>
            <p className="text-sm text-gray-500">Manage import sales contracts with suppliers</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchContracts}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
          <button
            onClick={() => setShowUploadModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg hover:opacity-90 transition shadow-md"
            data-testid="upload-pdf-btn"
          >
            <Upload size={18} />
            Upload PDF
          </button>
          <button
            onClick={openAddForm}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:opacity-90 transition shadow-md"
            data-testid="add-contract-btn"
          >
            <Plus size={18} />
            Add Manually
          </button>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-lg flex items-start gap-2 ${
          message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200'
          : message.type === 'duplicate' ? 'bg-amber-50 text-amber-900 border border-amber-300'
          : 'bg-red-50 text-red-700 border border-red-200'
        }`} data-testid="contract-save-message">
          {message.type === 'success' ? <CheckCircle size={20} className="mt-0.5 flex-shrink-0" /> : <AlertCircle size={20} className="mt-0.5 flex-shrink-0" />}
          <div className="flex-1">
            <div className="text-sm">{message.text}</div>
            {message.type === 'duplicate' && message.allowForce && (
              <div className="mt-2 flex gap-2">
                <button
                  type="button"
                  onClick={() => handleSubmit(null, true)}
                  className="text-xs px-3 py-1.5 rounded bg-amber-600 text-white hover:bg-amber-700"
                  data-testid="force-save-contract-btn"
                >
                  Save Anyway (create as duplicate)
                </button>
                <button
                  type="button"
                  onClick={() => setMessage(null)}
                  className="text-xs px-3 py-1.5 rounded bg-white border border-amber-300 text-amber-900 hover:bg-amber-100"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Duplicate Contract Number Warning */}
      {duplicateNumbers.size > 0 && (
        <div
          className="p-4 rounded-lg flex items-start gap-3 bg-amber-50 border border-amber-200 text-amber-900"
          data-testid="duplicate-contracts-warning"
        >
          <AlertCircle className="text-amber-600 shrink-0 mt-0.5" size={20} />
          <div className="text-sm">
            <p className="font-semibold">
              {duplicateNumbers.size} contract number(s) appear on more than one record
            </p>
            <p className="mt-0.5">
              Numbers:&nbsp;
              <span className="font-mono font-semibold">{[...duplicateNumbers].join(', ')}</span>.
              Review the highlighted rows below and delete the extra entry. New contracts are now
              protected — duplicates cannot be created anymore.
            </p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-4 bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
        <div className="flex-1 min-w-[200px] relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Search by contract number, supplier..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:border-blue-400 outline-none"
        >
          <option value="all">All Status</option>
          <option value="Active">Active</option>
          <option value="Pending">Pending</option>
          <option value="Completed">Completed</option>
          <option value="Expired">Expired</option>
        </select>
        {unemailedCount > 0 && (
          <button
            onClick={() => setShowOnlyUnemailed(v => !v)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition ${
              showOnlyUnemailed
                ? 'bg-amber-500 text-white border-amber-500 shadow-md hover:bg-amber-600'
                : 'bg-amber-50 text-amber-800 border-amber-300 hover:bg-amber-100'
            }`}
            title={showOnlyUnemailed
              ? `Showing only ${unemailedCount} unemailed contract${unemailedCount === 1 ? '' : 's'} — click to clear filter`
              : `${unemailedCount} contract${unemailedCount === 1 ? '' : 's'} not yet emailed — click to filter`}
            data-testid="sales-contract-unemailed-filter-badge"
          >
            <Mail size={14} />
            <span>{unemailedCount} not emailed{showOnlyUnemailed && ' ·'}</span>
            {showOnlyUnemailed && <X size={14} />}
          </button>
        )}
      </div>

      {/* Contracts Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Contract #</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Supplier</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Items</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Total Value</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Valid Until</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Email</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">PO</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                    <Loader2 className="animate-spin mx-auto mb-2" size={24} />
                    Loading contracts...
                  </td>
                </tr>
              ) : filteredContracts.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-4 py-8 text-center text-gray-500">
                    <FileUp className="mx-auto mb-2 text-gray-400" size={32} />
                    <p>No contracts found.</p>
                    <p className="text-sm">Upload a PDF or add a contract manually to get started.</p>
                  </td>
                </tr>
              ) : (
                filteredContracts.map((contract) => {
                  const isDup = duplicateNumbers.has((contract.contractNumber || '').trim());
                  return (
                  <tr key={contract.id} className={`hover:bg-gray-50 ${isDup ? 'bg-amber-50/60' : ''}`}>
                    <td className="px-4 py-3 font-medium text-blue-600">
                      <div className="flex items-center gap-2">
                        <span>{contract.contractNumber}</span>
                        {isDup && (
                          <span
                            title="Duplicate contract number — delete one of these to resolve"
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800 bg-amber-100 border border-amber-200 rounded"
                            data-testid={`duplicate-badge-${contract.id}`}
                          >
                            <AlertCircle size={10} /> DUP
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{contract.supplierName}</td>
                    <td className="px-4 py-3 text-gray-600">{getItemsSummary(contract.items)}</td>
                    <td className="px-4 py-3 text-gray-600">{contract.items?.[0]?.currency || 'USD'} {contract.totalValue}</td>
                    <td className="px-4 py-3 text-gray-600">{contract.validUntil}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(contract.status)}`}>
                        {contract.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <EmailStatusBadge row={contract} testIdSuffix={contract.id} />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => handleDownloadPO(contract)}
                          className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition"
                          title="Download Purchase Order"
                          data-testid={`download-po-${contract.id}`}
                        >
                          <Download size={16} />
                        </button>
                        <button
                          onClick={() => setEmailContract(contract)}
                          className="p-1.5 text-teal-700 hover:bg-teal-50 rounded-lg transition"
                          title="Email PO to Supplier"
                          data-testid={`sales-contract-email-btn-${contract.id}`}
                        >
                          <Mail size={16} />
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => { 
                            setEditingContract(contract); 
                            setFormData({
                              ...contract, 
                              items: contract.items || [emptyItem],
                              termsConditions: Array.isArray(contract.termsConditions) ? contract.termsConditions : (contract.termsConditions ? [contract.termsConditions] : [])
                            }); 
                            setShowForm(true); 
                          }}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => setHistoryContract(contract)}
                          className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-lg transition"
                          title="Edit History"
                          data-testid={`sales-contract-history-btn-${contract.id}`}
                        >
                          <History size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(contract.id)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* PDF Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900">Upload Sales Contract PDF</h2>
              <button onClick={() => setShowUploadModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={20} />
              </button>
            </div>
            <div className="p-6">
              <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-blue-400 transition">
                {uploading ? (
                  <div className="space-y-3">
                    <Loader2 className="animate-spin mx-auto text-blue-500" size={48} />
                    <p className="text-gray-600">Analyzing contract with AI...</p>
                    <p className="text-sm text-gray-500">This may take a few seconds</p>
                  </div>
                ) : (
                  <>
                    <FileUp className="mx-auto text-gray-400 mb-3" size={48} />
                    <p className="text-gray-600 mb-2">Drag and drop your PDF here, or</p>
                    <label className="inline-block px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg cursor-pointer hover:opacity-90 transition">
                      Browse Files
                      <input
                        type="file"
                        accept=".pdf"
                        onChange={handlePdfUpload}
                        className="hidden"
                      />
                    </label>
                    <p className="text-sm text-gray-500 mt-3">Supported format: PDF only</p>
                  </>
                )}
              </div>
              <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-700">
                  <strong>AI-Powered Extraction:</strong> The system will extract key details and match them with your master data. Commodity will be matched to ISRI codes.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <div>
                <h2 className="text-lg font-bold text-gray-900">
                  {editingContract ? 'Edit Contract' : (extractedData ? 'Review Extracted Details' : 'Add New Contract')}
                </h2>
                {extractedData && (
                  <p className="text-sm text-green-600 flex items-center gap-1">
                    <CheckCircle size={14} />
                    Extracted from: {extractedData.originalFileName}
                  </p>
                )}
              </div>
              <button onClick={resetForm} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Contract Details */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Contract Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Contract Number</label>
                    <input
                      type="text"
                      value={formData.contractNumber || nextContractNumber}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700"
                    />
                    <p className="text-xs text-gray-500 mt-1">Auto-generated</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Contract Date *</label>
                    <input
                      type="date"
                      value={formData.contractDate}
                      onChange={(e) => setFormData(prev => ({ ...prev, contractDate: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-400 outline-none"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-400 outline-none"
                    >
                      <option value="Active">Active</option>
                      <option value="Pending">Pending</option>
                      <option value="Completed">Completed</option>
                      <option value="Expired">Expired</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Supplier Details */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Supplier Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Supplier Name *</label>
                    <select
                      value={formData.supplierId}
                      onChange={(e) => handleSupplierChange(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-400 outline-none"
                      required
                    >
                      <option value="">Select supplier from master data...</option>
                      {supplierOptions.map(s => (
                        <option key={s.id} value={s.id}>{s.supplierName}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Supplier Country</label>
                    <input
                      type="text"
                      value={formData.supplierCountry}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                      placeholder="Auto-filled from master data"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Supplier Address</label>
                    <input
                      type="text"
                      value={formData.supplierAddress}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                      placeholder="Auto-filled from master data"
                    />
                  </div>
                </div>
              </div>

              {/* Items Section */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Contract Items</h3>
                  <button
                    type="button"
                    onClick={addItem}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition"
                  >
                    <Plus size={16} />
                    Add Item
                  </button>
                </div>
                
                <div className="space-y-4">
                  {formData.items.map((item, index) => (
                    <div key={index} className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-gray-600">Item {index + 1}</span>
                        {formData.items.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeItem(index)}
                            className="p-1 text-red-500 hover:bg-red-50 rounded"
                          >
                            <Minus size={16} />
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                        <div className="col-span-2">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Commodity (ISRI) *</label>
                          <select
                            value={item.commodity}
                            onChange={(e) => handleItemChange(index, 'commodity', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-400 outline-none text-sm"
                            required
                          >
                            <option value="">Select ISRI code...</option>
                            {ISRI_COMMODITIES.map(c => (
                              <option key={c} value={c}>{c}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Quantity *</label>
                          <input
                            type="number"
                            step="0.001"
                            value={item.quantity}
                            onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-400 outline-none text-sm"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Unit</label>
                          <select
                            value={item.quantityUnit}
                            onChange={(e) => handleItemChange(index, 'quantityUnit', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-400 outline-none text-sm"
                          >
                            <option value="MT">MT</option>
                            <option value="KG">KG</option>
                            <option value="LBS">LBS</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Rate</label>
                          <div className="flex">
                            <select
                              value={item.currency}
                              onChange={(e) => handleItemChange(index, 'currency', e.target.value)}
                              className="w-20 px-1 py-2 border border-r-0 border-gray-300 rounded-l-lg focus:border-blue-400 outline-none text-sm"
                            >
                              <option value="USD">USD</option>
                              <option value="EUR">EUR</option>
                              <option value="GBP">GBP</option>
                              <option value="INR">INR</option>
                              <option value="AED">AED</option>
                            </select>
                            <input
                              type="number"
                              step="0.01"
                              value={item.rate}
                              onChange={(e) => handleItemChange(index, 'rate', e.target.value)}
                              className="flex-1 px-2 py-2 border border-gray-300 rounded-r-lg focus:border-blue-400 outline-none text-sm"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Amount</label>
                          <input
                            type="text"
                            value={item.amount}
                            readOnly
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Total Value */}
                <div className="mt-4 flex justify-end">
                  <div className="bg-blue-50 px-4 py-2 rounded-lg">
                    <span className="text-sm text-gray-600">Total Contract Value: </span>
                    <span className="text-lg font-bold text-blue-600">
                      {formData.items[0]?.currency || 'USD'} {formData.totalValue || '0.00'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Validity & Terms */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Validity & Terms</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Valid From</label>
                    <input
                      type="date"
                      value={formData.validFrom}
                      onChange={(e) => setFormData(prev => ({ ...prev, validFrom: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-400 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Valid Until *</label>
                    <input
                      type="date"
                      value={formData.validUntil}
                      onChange={(e) => setFormData(prev => ({ ...prev, validUntil: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-400 outline-none"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Terms</label>
                    <select
                      value={formData.paymentTerms}
                      onChange={(e) => setFormData(prev => ({ ...prev, paymentTerms: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-400 outline-none"
                    >
                      <option value="">Select payment terms...</option>
                      {paymentTermsOptions.map(pt => (
                        <option key={pt.id} value={pt.paymentName || pt.description || pt.termDescription || pt.termTitle}>
                          {pt.paymentName || pt.description || pt.termDescription || pt.termTitle}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Delivery Terms</label>
                    <select
                      value={formData.deliveryTerms}
                      onChange={(e) => setFormData(prev => ({ ...prev, deliveryTerms: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-400 outline-none"
                    >
                      <option value="">Select...</option>
                      <option value="CIF">CIF</option>
                      <option value="CFR">CFR</option>
                      <option value="FOB">FOB</option>
                      <option value="EXW">EXW</option>
                      <option value="DAP">DAP</option>
                      <option value="DDP">DDP</option>
                    </select>
                  </div>
                </div>
                
                {/* Multiple Terms & Conditions Selection */}
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">Terms & Conditions</label>
                    {(() => {
                      const total = formData.termsConditions?.length || 0;
                      const valid = (formData.termsConditions || []).filter(id => termsConditionsList.find(t => t.id === id)).length;
                      const orphan = total - valid;
                      return (
                        <span className="text-xs text-gray-400">
                          {valid} selected
                          {orphan > 0 && (
                            <span className="ml-1 text-amber-700 italic" title={`${orphan} term(s) on this contract no longer exist in master data`}>
                              (+{orphan} deleted)
                            </span>
                          )}
                        </span>
                      );
                    })()}
                  </div>
                  
                  {/* Selected Terms Tags */}
                  {formData.termsConditions?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-2 p-2 bg-blue-50 rounded-lg">
                      {formData.termsConditions.map((termId, index) => {
                        const term = termsConditionsList.find(t => t.id === termId);
                        return term ? (
                          <div key={index} className="flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                            <span className="max-w-[200px] truncate" title={term.termDescription}>
                              {term.termDescription?.substring(0, 40) || term.termTitle}...
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                setFormData(prev => ({
                                  ...prev,
                                  termsConditions: prev.termsConditions.filter(id => id !== termId)
                                }));
                              }}
                              className="p-0.5 hover:bg-blue-200 rounded-full"
                            >
                              <X size={12} />
                            </button>
                          </div>
                        ) : null;
                      })}
                    </div>
                  )}
                  
                  {/* Add Terms Dropdown */}
                  <select
                    value=""
                    onChange={(e) => {
                      if (e.target.value && !formData.termsConditions?.includes(e.target.value)) {
                        setFormData(prev => ({
                          ...prev,
                          termsConditions: [...(prev.termsConditions || []), e.target.value]
                        }));
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-400 outline-none"
                  >
                    <option value="">+ Add terms & conditions...</option>
                    {termsConditionsList
                      .filter(t => !formData.termsConditions?.includes(t.id))
                      .map(tc => (
                        <option key={tc.id} value={tc.id}>
                          {tc.termDescription || tc.termTitle}
                        </option>
                      ))}
                  </select>
                </div>
              </div>

              {/* Shipping Details */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Shipping Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Port of Loading</label>
                    <input
                      type="text"
                      value={formData.portOfLoading}
                      onChange={(e) => setFormData(prev => ({ ...prev, portOfLoading: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-400 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Port of Discharge</label>
                    <input
                      type="text"
                      value={formData.portOfDischarge}
                      onChange={(e) => setFormData(prev => ({ ...prev, portOfDischarge: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-400 outline-none"
                    />
                  </div>
                </div>
              </div>

              {/* Remarks */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Remarks / Notes</label>
                <textarea
                  value={formData.remarks}
                  onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-400 outline-none"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:opacity-90 transition"
                >
                  {editingContract ? 'Update Contract' : 'Save Contract'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <EditHistoryModal
        open={!!historyContract}
        onClose={() => setHistoryContract(null)}
        title={historyContract ? `${historyContract.contractNumber} — Edit History` : 'Edit History'}
        historyUrl={historyContract ? `/imports/sales-contracts/${historyContract.id}/history` : null}
      />
      <EmailModal
        open={!!emailContract}
        onClose={() => setEmailContract(null)}
        title="Email Sales Contract PO to Supplier"
        subtitle={emailContract ? `Contract ${emailContract.contractNumber} → ${emailContract.supplierName || ''}` : ''}
        emailEndpoint={emailContract ? `/imports/sales-contracts/${emailContract.id}/email` : ''}
        defaultTo={emailContract?.supplierEmail || ''}
        defaultSubject={emailContract ? `Purchase Order — Sales Contract ${emailContract.contractNumber}` : ''}
        defaultBody={emailContract ? [
          `Dear ${emailContract.supplierName || 'Supplier'},`,
          '',
          `Please find attached the Purchase Order generated from our Sales Contract ${emailContract.contractNumber}.`,
          '',
          `Total Value: ${emailContract.items?.[0]?.currency || 'USD'} ${emailContract.totalValue || 0}`,
          '',
          'Kindly acknowledge receipt and confirm shipment as per the terms agreed.',
          '',
          'Regards,',
          'Swift Strips India',
        ].join('\n') : ''}
        onSent={() => fetchContracts()}
      />
    </div>
  );
}
